import { InvalidInputError, UnauthorizedError, UserRejectedRequestError, } from '@exodus/web3-errors';
import { connect } from '@exodus/web3-rpc-handlers';
import { createSignInMessageText, decodeMessage, deserializeEncodedMessage, deserializeTransaction, deserializeTransactionBytes, getTransactionId, isLegacyTransaction, isTransactionMessage, serializeMessageSignature, serializePublicKey, serializeTransaction, } from '@exodus/web3-solana-utils';
import bs58 from 'bs58';
import makeConcurrent from 'make-concurrent';
import assert from 'minimalistic-assert';
import { getSignerPublicKey } from '../provider/utils/transactions.js';
import { ExodusSolanaWalletAccount } from '../wallet-standard/account.js';
const NETWORK = 'solana';
export function exposeHandlers(rpc, deps) {
    const { isTrusted, ensureTrusted, isAutoApproved, approveConnection, approveMessage, approveTransactions, getPublicKey, getLatestBlockhash, getIsConnected, sendRawTransaction, onTransactionsSigned = () => { }, ensureUnlocked = async () => true, reportMaliciousSite = () => { }, getOrigin, getConnectedAccounts, getPathname, scanDomains, simulateSolanaTransactions, getAsset, isUnlocked, transactionSigner, getActiveWalletAccountData, getWalletAccountsData, messageSigner, addConnection, } = deps;
    async function assertTrusted() {
        const trusted = await isTrusted();
        if (!trusted) {
            throw new UnauthorizedError();
        }
    }
    async function approve(approvalFn, transactions) {
        const autoApproved = await isAutoApproved();
        if (autoApproved) {
            return;
        }
        const origin = getOrigin();
        const pathname = getPathname ? getPathname() : '';
        const walletAccountAddresses = await getWalletAddresses();
        const addresses = walletAccountAddresses.map(({ address }) => address);
        const publicKey = getSignerPublicKey(transactions[0], addresses);
        const simulationResults = await simulateSolanaTransactions({
            transactions,
            asset: await getAsset(NETWORK),
            senderAddress: publicKey?.toBase58() ?? (await getWalletAddress()),
            origin: origin + pathname,
        });
        const approved = await approvalFn(NETWORK, simulationResults);
        if (!approved) {
            throw new UserRejectedRequestError();
        }
    }
    async function getWalletAddress() {
        const publicKey = await getPublicKey();
        return serializePublicKey(publicKey);
    }
    async function signTransactions(transactions) {
        const walletAccounts = await getWalletAccountsData();
        const walletAccountAddresses = await getWalletAddresses();
        const addresses = walletAccountAddresses.map(({ address }) => address);
        const signParams = transactions.map((transaction) => {
            const address = getSignerPublicKey(transaction, addresses)?.toBase58();
            const accountName = address
                ? walletAccountAddresses.find(({ address: a }) => a === address)?.name
                : walletAccountAddresses[0].name;
            const walletAccount = accountName ? walletAccounts[accountName] : null;
            assert(walletAccount, `Wallet account for address ${address} not found`);
            return {
                baseAssetName: 'solana',
                walletAccount,
                unsignedTx: {
                    txData: {
                        transaction,
                        transactionBuffer: Buffer.from(transaction.serialize({ verifySignatures: false })),
                    },
                    txMeta: Object.create(null),
                },
            };
        });
        const signedTransactions = 'signTransactions' in transactionSigner &&
            typeof transactionSigner.signTransactions === 'function'
            ? await transactionSigner.signTransactions(signParams)
            : await Promise.all(signParams.map(async (params) => transactionSigner.signTransaction(params)));
        return signedTransactions.map(({ rawTx }) => deserializeTransactionBytes(Buffer.from(rawTx, 'base64')));
    }
    rpc.exposeFunction('sol_isConnected', async () => {
        if (!getIsConnected) {
            return null;
        }
        return getIsConnected();
    });
    rpc.exposeFunction('sol_connect', async ({ onlyIfTrusted, silent = false, }) => {
        await connect({
            approveConnection,
            ensureTrusted,
            isTrusted,
            ensureUnlocked,
            getOrigin,
            getPathname,
            scanDomains,
        }, NETWORK, onlyIfTrusted, silent);
        if (onlyIfTrusted) {
            const connectedAccounts = await getConnectedAccounts();
            if (connectedAccounts.length > 0) {
                return connectedAccounts.map(({ name, addresses }) => ({
                    name,
                    address: addresses.solana,
                }));
            }
            assert(typeof isUnlocked === 'function', 'Cannot determine lock state');
            const unlocked = await isUnlocked();
            assert(!silent || unlocked, 'Cannot connect silently when locked');
            if (!unlocked) {
                await ensureUnlocked();
            }
        }
        return getWalletAddresses();
    });
    const getWalletAccountDataByAddress = async (address) => {
        const data = await getWalletAccountsData();
        const walletAccountNames = Object.keys(data);
        const dataByAddress = Object.fromEntries(await Promise.all(walletAccountNames.map(async (name) => {
            const publicKey = await getPublicKey(name);
            return [publicKey.toBase58(), data[name]];
        })));
        return dataByAddress[address];
    };
    const getWalletAddresses = async () => {
        const data = await getWalletAccountsData();
        const activeWalletAccount = await getActiveWalletAccountData();
        const activeWalletAccountName = activeWalletAccount.toString();
        const walletAccountNames = Object.keys(data).filter((name) => name !== activeWalletAccountName);
        const addresses = await Promise.all(walletAccountNames.map(async (name) => {
            const publicKey = await getPublicKey(name);
            return { name, address: publicKey.toBase58() };
        }));
        addresses.unshift({
            name: activeWalletAccountName,
            address: await getWalletAddress(),
        });
        return addresses;
    };
    const getWalletAccountData = async (address) => {
        if (address) {
            const walletAccount = await getWalletAccountDataByAddress(address);
            assert(walletAccount, `Wallet account for address ${address} does not exist`);
            return {
                address,
                walletAccount,
            };
        }
        const activeAddress = await getWalletAddress();
        const walletAccount = await getActiveWalletAccountData();
        return { address: activeAddress, walletAccount };
    };
    rpc.exposeFunction('sol_signIn', async (input) => {
        await ensureUnlocked();
        const addresses = await getWalletAddresses();
        const loginAddress = input.address ?? addresses[0].address;
        const { walletAccount } = await getWalletAccountData(loginAddress);
        const message = createSignInMessageText({
            ...input,
            domain: input.domain ?? deps.getOrigin(),
            address: loginAddress,
        });
        const approved = await approveMessage(NETWORK, message);
        if (!approved) {
            throw new UserRejectedRequestError();
        }
        const rawMessage = Buffer.from(message, 'utf8');
        const signature = await messageSigner.signMessage({
            baseAssetName: NETWORK,
            walletAccount,
            message: { rawMessage },
        });
        const sorted = input.address
            ? addresses.sort((a) => (a.address === input.address ? -1 : 0))
            : addresses;
        await addConnection([NETWORK]);
        return {
            accounts: sorted.map(({ address }) => new ExodusSolanaWalletAccount({
                address,
                publicKey: bs58.decode(address),
            })),
            signature: signature.toString('base64'),
            signedMessage: rawMessage.toString('base64'),
        };
    });
    rpc.exposeFunction('sol_signTransaction', async (wireTransaction) => {
        await assertTrusted();
        const transaction = deserializeTransaction(wireTransaction);
        await approve(approveTransactions, [transaction]);
        const [signedTransaction] = await signTransactions([transaction]);
        try {
            // TODO: `transaction.partialSign()` may not guarantee `transaction.signature`. Double-check.
            const transactionId = getTransactionId(signedTransaction);
            onTransactionsSigned(NETWORK, [transactionId]);
        }
        catch {
            // Ignore error.
        }
        return serializeTransaction(signedTransaction);
    });
    rpc.exposeFunction('sol_signAllTransactions', async (wireTransactions) => {
        await assertTrusted();
        const transactions = wireTransactions.map(deserializeTransaction);
        await approve(approveTransactions, transactions);
        const signedTransactions = await signTransactions(transactions);
        try {
            // TODO: `transaction.partialSign()` may not guarantee `transaction.signature`. Double-check.
            const transactionIds = signedTransactions.map(getTransactionId);
            onTransactionsSigned(NETWORK, transactionIds);
        }
        catch {
            // Ignore error.
        }
        return signedTransactions.map(serializeTransaction);
    });
    rpc.exposeFunction('sol_signAndSendTransaction', async (wireTransaction, options = {}) => {
        await assertTrusted();
        const transaction = deserializeTransaction(wireTransaction);
        await approve(approveTransactions, [transaction]);
        const [signedTransaction] = await signTransactions([transaction]);
        const transactionId = getTransactionId(signedTransaction);
        onTransactionsSigned(NETWORK, [transactionId]);
        const rawTransaction = isLegacyTransaction(signedTransaction)
            ? signedTransaction.serialize()
            : Buffer.from(signedTransaction.serialize());
        await sendRawTransaction(rawTransaction, options);
        return transactionId;
    });
    rpc.exposeFunction('sol_signAndSendAllTransactions', async (inputs, options) => {
        assert(Array.isArray(inputs) && inputs.length > 0, 'Expected at least one transaction');
        await assertTrusted();
        const deserializedInputs = inputs.map(({ transaction, ...rest }) => ({
            ...rest,
            transaction: deserializeTransaction(transaction),
        }));
        const transactions = deserializedInputs.map(({ transaction }) => transaction);
        await approve(approveTransactions, transactions);
        const signedTransactions = await signTransactions(transactions);
        const signatures = signedTransactions.map((tx) => getTransactionId(tx));
        onTransactionsSigned(NETWORK, signatures);
        const send = options?.parallel
            ? sendRawTransaction
            : makeConcurrent(sendRawTransaction, { concurrency: 1 });
        return Promise.allSettled(signedTransactions.map(async (signedTransaction, index) => {
            const rawTransaction = isLegacyTransaction(signedTransaction)
                ? signedTransaction.serialize()
                : Buffer.from(signedTransaction.serialize());
            await send(rawTransaction, deserializedInputs[index].options);
            return signatures[index];
        }));
    });
    rpc.exposeFunction('sol_signMessage', async (...params) => {
        await assertTrusted();
        const [wireEncodedMessage, { display, address }] = params;
        const encodedMessage = deserializeEncodedMessage(wireEncodedMessage);
        if (isTransactionMessage(encodedMessage)) {
            reportMaliciousSite(); // Fire and forget.
            throw new InvalidInputError();
        }
        const { walletAccount } = await getWalletAccountData(address);
        const message = decodeMessage(encodedMessage, display);
        const approved = await approveMessage(NETWORK, message, walletAccount.toString());
        if (!approved) {
            throw new UserRejectedRequestError();
        }
        const signature = await messageSigner.signMessage({
            baseAssetName: 'solana',
            walletAccount,
            message: { rawMessage: Buffer.from(encodedMessage) },
        });
        return serializeMessageSignature(signature);
    });
    rpc.exposeFunction('sol_getLatestBlockhash', getLatestBlockhash);
}
